import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { usePermissions } from '@/hooks/usePermissions';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { AlertTriangle } from 'lucide-react';
import { ProtectedRoute } from '@/components/ProtectedRoute';

interface ProdutoEstoqueBaixo {
  id: string;
  codigo_produto: string;
  nome_produto: string;
  categoria: string;
  quantidade_minima: number;
  saldo_atual: number;
  diferenca: number;
}

export default function EstoqueBaixo() {
  const [produtosBaixo, setProdutosBaixo] = useState<ProdutoEstoqueBaixo[]>([]);
  const [loading, setLoading] = useState(true);
  const { hasPermission } = usePermissions();

  const canReadEstoqueBaixo = hasPermission('produtos_insumos', 'estoque_baixo');

  useEffect(() => {
    if (canReadEstoqueBaixo) {
      fetchProdutosEstoqueBaixo();
    } else {
      setLoading(false);
    }
  }, [canReadEstoqueBaixo]);

  const fetchProdutosEstoqueBaixo = async () => {
    try {
      setLoading(true);

      // Primeiro, buscar todos os produtos ativos
      // @ts-ignore - Temporary fix for deep recursion type error
      const { data: produtos, error: produtosError } = await supabase
        .from('produtos_insumos')
        .select('id, codigo_produto, nome_produto, categoria, quantidade_minima')
        .eq('ativo', true);

      if (produtosError) throw produtosError;

      if (!produtos || produtos.length === 0) {
        setProdutosBaixo([]);
        return;
      }

      // Para cada produto, calcular o saldo atual
      const produtosComSaldo: ProdutoEstoqueBaixo[] = [];

      for (const produto of produtos) {
        // Buscar todas as movimentações do produto
        const { data: movimentacoes, error: movError } = await supabase
          .from('produtos_entrada_saida')
          .select('tipo_movimentacao, quantidade')
          .eq('produto_id', produto.id);

        if (movError) {
          console.error('Erro ao buscar movimentações:', movError);
          continue;
        }

        // Calcular saldo atual
        let saldoAtual = 0;
        if (movimentacoes) {
          saldoAtual = movimentacoes.reduce((saldo, mov) => {
            if (mov.tipo_movimentacao === 'entrada') {
              return saldo + mov.quantidade;
            } else {
              return saldo - mov.quantidade;
            }
          }, 0);
        }

        // Verificar se está abaixo do mínimo
        if (saldoAtual < produto.quantidade_minima) {
          produtosComSaldo.push({
            id: produto.id,
            codigo_produto: produto.codigo_produto,
            nome_produto: produto.nome_produto,
            categoria: produto.categoria,
            quantidade_minima: produto.quantidade_minima,
            saldo_atual: saldoAtual,
            diferenca: produto.quantidade_minima - saldoAtual
          });
        }
      }

      // Ordenar por diferença (maior diferença primeiro)
      produtosComSaldo.sort((a, b) => b.diferenca - a.diferenca);

      setProdutosBaixo(produtosComSaldo);
    } catch (error) {
      console.error('Erro ao buscar produtos com estoque baixo:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!canReadEstoqueBaixo) {
    return (
      <ProtectedRoute>
        <div className="container mx-auto p-6">
          <Card>
            <CardContent className="flex items-center justify-center h-32">
              <p className="text-muted-foreground">Você não tem permissão para acessar esta página.</p>
            </CardContent>
          </Card>
        </div>
      </ProtectedRoute>
    );
  }

  return (
    <ProtectedRoute>
      <div className="container mx-auto p-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-orange-500" />
              Estoque Baixo
            </CardTitle>
            <p className="text-sm text-muted-foreground">
              Produtos com estoque abaixo da quantidade mínima cadastrada
            </p>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              </div>
            ) : produtosBaixo.length === 0 ? (
              <div className="text-center py-8">
                <AlertTriangle className="mx-auto h-12 w-12 text-green-500 mb-4" />
                <p className="text-lg font-medium text-green-600">Parabéns!</p>
                <p className="text-muted-foreground">Todos os produtos estão com estoque adequado.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Código</TableHead>
                      <TableHead>Produto</TableHead>
                      <TableHead>Categoria</TableHead>
                      <TableHead className="text-center">Estoque Atual</TableHead>
                      <TableHead className="text-center">Mínimo</TableHead>
                      <TableHead className="text-center">Diferença</TableHead>
                      <TableHead className="text-center">Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {produtosBaixo.map((produto) => (
                      <TableRow key={produto.id}>
                        <TableCell className="font-mono text-sm">
                          {produto.codigo_produto}
                        </TableCell>
                        <TableCell className="font-medium">
                          {produto.nome_produto}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">
                            {produto.categoria}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-center">
                          <span className="font-medium text-red-600">
                            {produto.saldo_atual}
                          </span>
                        </TableCell>
                        <TableCell className="text-center">
                          {produto.quantidade_minima}
                        </TableCell>
                        <TableCell className="text-center">
                          <Badge variant="destructive">
                            -{produto.diferenca}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-center">
                          <Badge variant="destructive" className="gap-1">
                            <AlertTriangle className="h-3 w-3" />
                            Crítico
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </ProtectedRoute>
  );
}